import { useState, useEffect, useRef } from "react";
import impersonationService from "../../services/impersonationService";
import { readRecentTargets } from "../../utils/impersonationStorage";
import { useDebounce } from "../../hooks/useDebounce";

// Superuser-only user picker for the "View as" role preview (TP-View-As).
// Clone of AgencySearchModal backed by the impersonation search endpoint:
// searches ALL accounts by email (USERNAME_FIELD) as well as name, rows show
// name · email · role · club/agency. With an empty query the last 5 preview
// targets (localStorage) are offered for fast persona switching.
const AdminUserSearchModal = ({ isOpen, onClose, onUserSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentTargets, setRecentTargets] = useState([]);

  const searchInputRef = useRef(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (debouncedSearchQuery.trim() && isOpen) {
      searchUsers(debouncedSearchQuery);
    } else if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery, isOpen]);

  const searchUsers = async (query) => {
    setIsSearching(true);
    try {
      const results = await impersonationService.searchUsers(query, 20);
      setSearchResults(results);
    } catch (error) {
      console.error("View-as user search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setRecentTargets(readRecentTargets());
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const renderUserRow = (user) => (
    <button
      key={user.account_id}
      type="button"
      onClick={() => onUserSelect(user)}
      className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0">
          {user.avatar_url && (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-medium text-[#1A212F] truncate">
              {user.name}
            </h3>
            {user.role_label && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex-shrink-0">
                {user.role_label}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            {user.email && <span className="truncate">{user.email}</span>}
            {user.org_label && (
              <span className="flex-shrink-0 truncate">{user.org_label}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-[#202630]">View as user</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by email, name, role, club or agency..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg
              className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {isSearching && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}
          </div>
        </div>

        {/* Results / Recent */}
        <div className="flex-1 overflow-y-auto p-6">
          {!searchQuery.trim() && recentTargets.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Recent</p>
              <div className="space-y-2">{recentTargets.map(renderUserRow)}</div>
            </div>
          )}

          {isSearching && searchResults.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                <p className="text-secondary">Searching users...</p>
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">{searchResults.map(renderUserRow)}</div>
          ) : searchQuery.trim() && !isSearching ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-gray-300 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <p className="text-secondary">
                  No users found for "{searchQuery}"
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Try searching by exact email
                </p>
              </div>
            </div>
          ) : recentTargets.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-gray-300 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <p className="text-secondary">
                  Start typing to search for a user
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Search by email, name, role, club or agency
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminUserSearchModal;
